import { useEffect, useState } from "react";
import {
  FaArrowLeft,
  FaCheckCircle,
  FaLock,
  FaShieldAlt,
  FaShoppingBag,
  FaTrash,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";

import EmptyImage from "../assets/cart1.png";
import api from "../api";

export default function Cart({ setShowModal }) {
  const navigate = useNavigate();

  const [user] = useState(() => {
    const stored = localStorage.getItem("user");
    return stored ? JSON.parse(stored) : null;
  });

  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const isLoggedIn = !!user;

  // =========================================================
  // FETCH CART
  // =========================================================

  const fetchCart = async () => {
    if (!isLoggedIn) {
      setCartItems([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      const res = await api.get("/api/carts/", {
        headers: {
          Authorization: `Bearer ${user?.access}`,
        },
      });

      const data = Array.isArray(res.data)
        ? res.data
        : [];

      const userId = user?.pk || user?.id;

      const userCart = data.find(
        (cart) =>
          Number(cart.user) === Number(userId) &&
          cart.is_active === true
      );

      const items = userCart?.items || [];

      // =====================================================
      // REMOVE SOLD PRODUCTS FROM CART
      // =====================================================

      const soldItems = items.filter(
        (item) => item.is_sold === true
      );

      const availableItems = items.filter(
        (item) => item.is_sold !== true
      );

      // Delete sold items from backend cart
      if (soldItems.length > 0) {
        await Promise.allSettled(
          soldItems.map((item) =>
            api.delete(
              `/api/carts/remove/${item.id}/`,
              {
                headers: {
                  Authorization: `Bearer ${user?.access}`,
                },
              }
            )
          )
        );
      }

      // Only show available products
      setCartItems(availableItems);

    } catch (err) {
      console.error(
        "Cart error:",
        err.response?.data || err
      );

      setCartItems([]);

    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCart();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // =========================================================
  // REMOVE ITEM
  // =========================================================

  const removeItem = async (itemId) => {
    const token = user?.access;

    if (!token) return;

    try {
      await api.delete(
        `/api/carts/remove/${itemId}/`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setCartItems((prev) =>
        prev.filter(
          (item) => item.id !== itemId
        )
      );

    } catch (err) {
      console.error(
        "Remove error:",
        err.response?.data || err
      );
    }
  };

  // =========================================================
  // LOADING
  // =========================================================

  if (loading) {
    return (
      <section className="min-h-screen bg-[#F7F3EB] flex items-center justify-center px-4">
        <div className="text-center">

          <div className="mx-auto mb-5 h-10 w-10 animate-spin rounded-full border-4 border-[#D9D0C5] border-t-[#0F766E]" />

          <p className="text-sm font-medium text-[#76695D]">
            Loading your cart...
          </p>

        </div>
      </section>
    );
  }

  // =========================================================
  // NOT LOGGED IN
  // =========================================================

  if (!isLoggedIn) {
    return (
      <section className="min-h-screen bg-[#F7F3EB] px-4 py-16 sm:px-6 lg:px-8">

        <div className="mx-auto flex min-h-[70vh] max-w-xl items-center justify-center">

          <div className="w-full rounded-[2rem] border border-[#E7DED2] bg-white px-6 py-12 text-center shadow-[0_20px_60px_rgba(57,45,35,0.08)] sm:px-12">

            <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-[#EAF3F1] text-[#0F766E]">
              <FaShoppingBag className="text-2xl" />
            </div>

            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#0F766E]">
              A1 Rugs
            </p>

            <h1 className="mt-3 text-3xl font-semibold tracking-tight text-[#332A24]">
              Your shopping bag
            </h1>

            <p className="mx-auto mt-3 max-w-md text-sm leading-6 text-[#76695D]">
              Please sign in to view the rugs you've
              added to your cart.
            </p>

            <button
              type="button"
              onClick={() => setShowModal(true)}
              className="
                mt-8
                rounded-xl
                bg-[#0F766E]
                px-8
                py-3.5
                text-sm
                font-semibold
                text-white
                transition
                hover:-translate-y-0.5
                hover:bg-[#0C625C]
                hover:shadow-lg
              "
            >
              Sign In
            </button>

          </div>

        </div>

      </section>
    );
  }

  // =========================================================
  // EMPTY CART
  // =========================================================

  if (cartItems.length === 0) {
    return (
      <section className="min-h-screen bg-[#F7F3EB] px-4 py-16 sm:px-6 lg:px-8">

        <div className="mx-auto flex min-h-[70vh] max-w-2xl items-center justify-center">

          <div className="w-full rounded-[2rem] border border-[#E7DED2] bg-white px-6 py-14 text-center shadow-[0_20px_60px_rgba(57,45,35,0.07)] sm:px-12">

            <div className="mx-auto mb-7 flex h-20 w-20 items-center justify-center rounded-full bg-[#F3EEE7]">

              <img
                src={EmptyImage}
                alt="Empty cart"
                className="h-12 w-12 object-contain opacity-80"
              />

            </div>

            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#0F766E]">
              A1 Rugs
            </p>

            <h1 className="mt-3 text-3xl font-semibold tracking-tight text-[#332A24]">
              Your cart is empty
            </h1>

            <p className="mx-auto mt-3 max-w-md text-sm leading-6 text-[#76695D]">
              Start exploring our handmade Afghan rugs
              and add your favorites to your cart.
            </p>

            <button
              type="button"
              onClick={() => window.history.back()}
              className="
                mt-8
                inline-flex
                items-center
                gap-2
                rounded-xl
                bg-[#0F766E]
                px-7
                py-3.5
                text-sm
                font-semibold
                text-white
                transition
                hover:-translate-y-0.5
                hover:bg-[#0C625C]
                hover:shadow-lg
              "
            >
              <FaArrowLeft className="text-xs" />
              Continue Shopping
            </button>

          </div>

        </div>

      </section>
    );
  }

  // =========================================================
  // TOTAL
  // =========================================================

  const cartTotal = cartItems
    .reduce(
      (sum, item) =>
        sum + Number(item.total_price || 0),
      0
    )
    .toFixed(2);

  // =========================================================
  // CART UI
  // =========================================================

  return (
    <section className="min-h-screen bg-[#F7F3EB] px-4 py-8 sm:px-6 sm:py-12 lg:px-8 lg:py-16">

      <div className="mx-auto max-w-7xl">

        {/* BACK */}

        <button
          type="button"
          onClick={() => window.history.back()}
          className="
            mb-8
            inline-flex
            items-center
            gap-2
            text-sm
            font-medium
            text-[#6F665D]
            transition
            hover:text-[#0F766E]
          "
        >
          <FaArrowLeft className="text-xs" />
          Continue Shopping
        </button>

        {/* HEADER */}

        <div className="mb-10 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">

          <div>

            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-[#0F766E]">
              Your selection
            </p>

            <h1 className="mt-2 text-4xl font-semibold tracking-tight text-[#332A24] sm:text-5xl">
              Shopping Cart
            </h1>

            <p className="mt-3 text-sm text-[#76695D]">
              Review your selected rugs before checkout.
            </p>

          </div>

          <div className="flex items-center gap-2 text-sm text-[#76695D]">

            <FaShoppingBag className="text-[#0F766E]" />

            <span>
              {cartItems.length}{" "}
              {cartItems.length === 1
                ? "item"
                : "items"}
            </span>

          </div>

        </div>

        {/* MAIN */}

        <div className="grid items-start gap-8 lg:grid-cols-[minmax(0,1fr)_380px]">

          {/* LEFT */}

          <div>

            <div className="overflow-hidden rounded-[1.75rem] border border-[#E7DED2] bg-white shadow-[0_20px_55px_rgba(57,45,35,0.06)]">

              <div className="border-b border-[#EFE8DF] px-5 py-5 sm:px-7">

                <div className="flex items-center justify-between">

                  <div>

                    <h2 className="text-lg font-semibold text-[#332A24]">
                      Your items
                    </h2>

                    <p className="mt-1 text-xs text-[#8A7D70]">
                      Handmade rugs selected for your order
                    </p>

                  </div>

                  <span className="rounded-full bg-[#EEF6F4] px-3 py-1.5 text-xs font-semibold text-[#0F766E]">
                    {cartItems.length}
                  </span>

                </div>

              </div>

              {/* PRODUCTS */}

              <div className="divide-y divide-[#EFE8DF]">

                {cartItems.map((item) => (

                  <div
                    key={item.id}
                    className="
                      group
                      p-5
                      transition
                      duration-200
                      hover:bg-[#FCFAF7]
                      sm:p-7
                    "
                  >

                    <div className="flex flex-col gap-5 sm:flex-row">

                      {/* IMAGE */}

                      <div className="relative h-36 w-full shrink-0 overflow-hidden rounded-2xl bg-[#F3EEE7] sm:h-32 sm:w-32">

                        <img
                          src={item.product_image}
                          alt={item.product_name}
                          className="
                            h-full
                            w-full
                            object-cover
                            transition
                            duration-500
                            group-hover:scale-[1.03]
                          "
                        />

                      </div>

                      {/* DETAILS */}

                      <div className="min-w-0 flex-1">

                        <div className="flex items-start justify-between gap-4">

                          <div className="min-w-0">

                            <h3 className="line-clamp-2 text-lg font-semibold leading-6 text-[#332A24]">
                              {item.product_name}
                            </h3>

                            {item.product_id && (
                              <p className="mt-1 text-xs text-[#A39487]">
                                Product ID: {item.product_id}
                              </p>
                            )}

                          </div>

                          <button
                            type="button"
                            onClick={() =>
                              removeItem(item.id)
                            }
                            aria-label={`Remove ${item.product_name}`}
                            className="
                              inline-flex
                              h-9
                              w-9
                              shrink-0
                              items-center
                              justify-center
                              rounded-full
                              text-[#9B8F84]
                              transition
                              hover:bg-red-50
                              hover:text-red-500
                            "
                          >
                            <FaTrash className="text-sm" />
                          </button>

                        </div>

                        {/* META */}

                        <div className="mt-4 flex flex-wrap gap-x-7 gap-y-2 text-sm text-[#76695D]">

                          <span>
                            Quantity:{" "}
                            <strong className="font-medium text-[#403831]">
                              {item.quantity}
                            </strong>
                          </span>

                          <span>
                            Price:{" "}
                            <strong className="font-medium text-[#403831] line-through">
                              $
                              {Number(
                                item.original_price || 0
                              ).toFixed(2)}
                            </strong>
                          </span>

                        </div>

                        {/* BOTTOM */}

                        <div className="mt-6 flex flex-wrap items-end justify-between gap-4">

                          <div className="flex items-center gap-2 text-xs text-[#8A7D70]">

                            <FaCheckCircle className="text-[#0F766E]" />

                            Available for purchase

                          </div>

                          <div className="text-right">

                            <p className="text-xs text-[#A39487]">
                              Item total
                            </p>

                            <p className="mt-1 text-xl font-semibold text-[#332A24]">
                              $
                              {Number(
                                item.total_price || 0
                              ).toFixed(2)}
                            </p>

                          </div>

                        </div>

                      </div>

                    </div>

                  </div>

                ))}

              </div>

            </div>

            {/* TRUST FEATURES */}

            <div className="mt-6 grid gap-4 sm:grid-cols-3">

              <div className="rounded-2xl border border-[#E7DED2] bg-white p-5">

                <div className="flex items-center gap-3">

                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#EEF6F4] text-[#0F766E]">
                    <FaShieldAlt />
                  </div>

                  <div>

                    <p className="text-sm font-semibold text-[#403831]">
                      Secure checkout
                    </p>

                    <p className="mt-0.5 text-xs text-[#8A7D70]">
                      Protected payment
                    </p>

                  </div>

                </div>

              </div>

              <div className="rounded-2xl border border-[#E7DED2] bg-white p-5">

                <div className="flex items-center gap-3">

                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#F5F0E9] text-[#6E5F50]">
                    <FaShoppingBag />
                  </div>

                  <div>

                    <p className="text-sm font-semibold text-[#403831]">
                      Carefully packed
                    </p>

                    <p className="mt-0.5 text-xs text-[#8A7D70]">
                      Ready for delivery
                    </p>

                  </div>

                </div>

              </div>

              <div className="rounded-2xl border border-[#E7DED2] bg-white p-5">

                <div className="flex items-center gap-3">

                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#F5F0E9] text-[#6E5F50]">
                    <FaLock />
                  </div>

                  <div>

                    <p className="text-sm font-semibold text-[#403831]">
                      Private & protected
                    </p>

                    <p className="mt-0.5 text-xs text-[#8A7D70]">
                      Your details stay safe
                    </p>

                  </div>

                </div>

              </div>

            </div>

          </div>

          {/* RIGHT */}

          <aside className="lg:sticky lg:top-24">

            <div className="overflow-hidden rounded-[1.75rem] border border-[#DDD2C4] bg-white shadow-[0_25px_70px_rgba(57,45,35,0.10)]">

              <div className="border-b border-[#EFE8DF] px-6 py-6 sm:px-7">

                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#0F766E]">
                  Order summary
                </p>

                <h2 className="mt-2 text-2xl font-semibold text-[#332A24]">
                  Your order
                </h2>

              </div>

              <div className="p-6 sm:p-7">

                {/* MINI PRODUCTS */}

                <div className="space-y-4 border-b border-[#EFE8DF] pb-6">

                  {cartItems.map((item) => (

                    <div
                      key={`summary-${item.id}`}
                      className="flex items-center gap-3"
                    >

                      <div className="h-12 w-12 shrink-0 overflow-hidden rounded-xl bg-[#F3EEE7]">

                        <img
                          src={item.product_image}
                          alt={item.product_name}
                          className="h-full w-full object-cover"
                        />

                      </div>

                      <div className="min-w-0 flex-1">

                        <p className="truncate text-sm font-medium text-[#403831]">
                          {item.product_name}
                        </p>

                        <p className="mt-0.5 text-xs text-[#8A7D70]">
                          Qty {item.quantity}
                        </p>

                      </div>

                      <p className="text-sm font-semibold text-[#403831]">

                        $
                        {Number(
                          item.total_price || 0
                        ).toFixed(2)}

                      </p>

                    </div>

                  ))}

                </div>

                {/* PRICE */}

                <div className="space-y-4 border-b border-[#EFE8DF] py-6">

                  <div className="flex items-center justify-between text-sm">

                    <span className="text-[#76695D]">
                      Subtotal
                    </span>

                    <span className="font-medium text-[#403831]">
                      ${cartTotal}
                    </span>

                  </div>

                  <div className="flex items-center justify-between gap-4 text-sm">

                    <span className="text-[#76695D]">
                      Shipping
                    </span>

                    <span className="text-right font-semibold text-[#0F766E]">
                      FREE
                    </span>

                  </div>

                </div>

                {/* TOTAL */}

                <div className="flex items-end justify-between py-6">

                  <div>

                    <p className="text-sm text-[#76695D]">
                      Total
                    </p>

                    <p className="mt-1 text-3xl font-bold tracking-tight text-[#332A24]">
                      ${cartTotal}
                    </p>

                  </div>

                  <span className="text-xs text-[#A39487]">
                    USD
                  </span>

                </div>

                {/* CHECKOUT */}

                <button
                  type="button"
                  onClick={() => navigate("/checkout")}
                  className="
                    w-full
                    rounded-xl
                    bg-[#0F766E]
                    px-5
                    py-4
                    text-sm
                    font-semibold
                    text-white
                    shadow-[0_12px_30px_rgba(15,118,110,0.20)]
                    transition
                    duration-200
                    hover:-translate-y-0.5
                    hover:bg-[#0C625C]
                    hover:shadow-[0_18px_35px_rgba(15,118,110,0.24)]
                  "
                >
                  Proceed to Checkout
                </button>

                {/* SECURITY NOTE */}

                <div className="mt-4 flex items-start gap-3 rounded-xl bg-[#F8F5F0] p-4">

                  <FaLock className="mt-0.5 shrink-0 text-sm text-[#0F766E]" />

                  <p className="text-xs leading-5 text-[#76695D]">
                    Your payment and shipping information
                    will be collected securely at checkout.
                  </p>

                </div>

                <div className="mt-6 text-center">

                  <p className="text-xs text-[#A39487]">
                    Handmade rugs • Secure payments • A1 Rugs
                  </p>

                </div>

              </div>

            </div>

          </aside>

        </div>

      </div>

    </section>
  );
}